import { BackendFetch, DevTools, I18nextPlugin, Tolgee, withTolgee } from '@tolgee/i18next';
import i18n, { type InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import I18NextChainedBackend, { type ChainedBackendOptions } from 'i18next-chained-backend';
import HttpBackend, { type HttpBackendOptions } from 'i18next-http-backend';
import ICU from 'i18next-icu';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';

import { CON_NAME } from '@/lib/constants.ts';
import { LANGUAGES } from '@/lib/hooks/useLanguage.ts';

import enTranslations from '../../../public/locales/en.json?url' with { type: 'json' };

// beta languages show in dev env
const betaLanguages = Object.entries(LANGUAGES).filter(([, data]) => data.beta === true);
const releasedLanguages = Object.entries(LANGUAGES).filter(([, data]) => data.beta !== true);
const enableNewTranslations = import.meta.env.PUBLIC_ENABLE_NEW_TRANSLATIONS === 'true';
export const ENABLED_LANGUAGES = [
  ...releasedLanguages,
  ...(enableNewTranslations ? betaLanguages : []),
];

const DEFAULT_NS = 'common';

const options: InitOptions<HttpBackendOptions & ChainedBackendOptions> = {
  //lng: 'en-US',
  fallbackLng: {
    en: ['en'],
    de: ['de', 'en'],
    jp: ['ja', 'en'],
    id: ['id', 'en'],
    es: ['es', 'en'],
    fr: ['fr', 'en'],
    pt: ['pt', 'en'],
    pl: ['pl', 'en'],
    nl: ['nl', 'en'],
    ru: ['ru', 'en'],
    default: ['en'],
  },
  supportedLngs: ENABLED_LANGUAGES.map(([code]) => code),
  //debug: process.env.NODE_ENV === 'development',
  debug: false,
  preload: ['en'],
  ns: DEFAULT_NS,
  defaultNS: DEFAULT_NS,
  interpolation: {
    // not needed for react as it escapes translations by default.
    escapeValue: false,
    // more of a formality, since the use(ICU) call makes defaults to this prefix and suffix.
    prefix: '{',
    suffix: '}',
    defaultVariables: {
      conName: CON_NAME,
    },
  },
  react: {
    useSuspense: false,
  },

  backend: {
    backends: [
      HttpBackend,
      resourcesToBackend({
        en: {
          common: enTranslations,
        },
      }),
    ],
    backendOptions: [
      {
        loadPath: (lang: string[], ns: string[]) =>
          lang
            .flatMap((l) =>
              ns.map((n) => (n === DEFAULT_NS ? `/locales/${l}.json` : `/locales/${n}/${l}.json`)),
            )
            .join(','),
      },
    ],
  },
};

const tolgee = Tolgee()
  .use(DevTools())
  .use(I18nextPlugin())
  .use(BackendFetch())
  .init({
    apiUrl: import.meta.env.PUBLIC_TOLGEE_API_URL,
    defaultLanguage: 'en',
  });

void withTolgee(i18n, tolgee)
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(I18NextChainedBackend)
  .use(ICU)
  .init(options);

export default i18n;
